import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MONTHS, YEARS } from "@/lib/constants";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, ResponsiveContainer } from "recharts";

export function PageHeader({ icon: Icon, title, subtitle, right, iconColor = "#0b1220" }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-lg bg-[#0b1220] flex items-center justify-center mt-1">
            <Icon className="w-6 h-6 text-amber-400" />
          </div>
        )}
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">{right}</div>
    </div>
  );
}

export function MonthYearFilter({ month, setMonth, year, setYear }) {
  return (
    <div className="flex items-center gap-3">
      <div className="bg-white rounded-lg border border-slate-200 px-1">
        <div className="text-[10px] text-slate-400 px-2 pt-1">Month</div>
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="border-0 h-7 min-w-[120px] font-semibold" data-testid="filter-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 px-1">
        <div className="text-[10px] text-slate-400 px-2 pt-1">Year</div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="border-0 h-7 min-w-[90px] font-semibold" data-testid="filter-year"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function SectionCard({ title, right, children, className = "", fill = false }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-5 ${fill ? "flex flex-col" : ""} ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">{title}</h3>
          {right}
        </div>
      )}
      {fill ? <div className="flex-1 min-h-0 relative"><div className="max-h-[60vh] overflow-y-auto lg:absolute lg:inset-0 lg:max-h-none">{children}</div></div> : children}
    </div>
  );
}

function sparkData(seed, color) {
  const arr = [];
  let v = 5;
  for (let i = 0; i < 24; i++) {
    v = Math.max(1, v + (Math.sin(seed + i) * 3));
    arr.push({ v: Math.abs(v) + (i % 3) * 2 });
  }
  return arr;
}

export function StatCard({ icon: Icon, iconBg, iconColor, label, value, change, last, extra, sparkType = "bar", sparkColor = "#2563eb", seed = 1 }) {
  const up = change >= 0;
  const data = sparkData(seed, sparkColor);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col justify-between" data-testid={`stat-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: iconBg }}>
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-slate-500 font-medium truncate">{label}</p>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-extrabold text-slate-900 leading-none">{value}</span>
            {change !== undefined && change !== null && (
              <span className={`text-xs font-semibold ${up ? "text-green-600" : "text-red-500"}`}>
                {up ? "↑" : "↓"} {Math.abs(change)}%
              </span>
            )}
          </div>
          {last !== undefined && <p className="text-xs text-slate-400 mt-0.5">vs Last Month: {last}</p>}
          {extra && <p className="text-xs text-slate-500 font-medium mt-0.5">{extra}</p>}
        </div>
      </div>
      <div className="h-10 mt-2 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          {sparkType === "line" ? (
            <LineChart data={data}><Line type="monotone" dataKey="v" stroke={sparkColor} strokeWidth={2} dot={false} /></LineChart>
          ) : sparkType === "area" ? (
            <AreaChart data={data}><Area type="monotone" dataKey="v" stroke={sparkColor} fill={sparkColor} fillOpacity={0.15} strokeWidth={2} /></AreaChart>
          ) : (
            <BarChart data={data}><Bar dataKey="v" fill={sparkColor} radius={[2, 2, 0, 0]} /></BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function Footer() {
  return null;
}
